/**
 * Role / permission store for the admin app — DEMO MODE.
 *
 * In real production builds the active user's role is dictated by InstantDB
 * (see `instant.perms.ts`). For the mock-mode demo surface we want recruiters
 * and stakeholders to be able to flip between roles in the topbar and watch
 * the UI re-shape itself accordingly. That flow is driven entirely by this store.
 *
 * Only the role key and custom permissions are persisted to localStorage. The
 * full permission set is derived on every set() from DEFAULT_ROLE_PERMISSIONS
 * so changes to that mapping in @bizop/shared/rbac flow through automatically.
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

import {
  DEFAULT_ROLE_PERMISSIONS,
  ROLE_KEYS,
  type PermissionKey,
  type RoleKey,
} from "@bizop/shared/rbac";

export type DemoRoleKey = Exclude<RoleKey, "dealer">;

/** Roles selectable from the dev role switcher. Dealer is a separate app. */
export const SELECTABLE_DEMO_ROLES: ReadonlyArray<DemoRoleKey> = [
  ROLE_KEYS.ADMIN,
  ROLE_KEYS.OWNER,
  ROLE_KEYS.FINANCE,
  ROLE_KEYS.FACTORY_MANAGER,
  ROLE_KEYS.OFFICE_MANAGER,
  ROLE_KEYS.SALES,
  ROLE_KEYS.WAREHOUSE,
];

/** Display labels for the role chip and Users page. */
export const ROLE_LABELS: Record<RoleKey, string> = {
  owner: "Owner",
  admin: "Admin",
  finance: "Finance",
  factory_manager: "Factory Manager",
  office_manager: "Office Manager",
  sales: "Sales",
  warehouse: "Warehouse",
  dealer: "Dealer",
};

const DEFAULT_ROLE: DemoRoleKey = ROLE_KEYS.ADMIN;

const VALID_DEMO_ROLES = new Set<string>(SELECTABLE_DEMO_ROLES);

/** Resolve effective permissions: custom overrides take precedence. */
function resolvePermissions(
  role: DemoRoleKey,
  customPermissions: Record<string, PermissionKey[]> | null,
): PermissionKey[] {
  if (customPermissions && customPermissions[role]) {
    return [...customPermissions[role]];
  }
  return [...DEFAULT_ROLE_PERMISSIONS[role]];
}

type RoleState = {
  currentRole: DemoRoleKey;
  permissions: PermissionKey[];
  customPermissions: Record<string, PermissionKey[]> | null;
  setRole: (role: DemoRoleKey) => void;
  setCustomPermissions: (role: string, perms: PermissionKey[]) => void;
  clearCustomPermissions: (role: string) => void;
};

type PersistedRoleState = Pick<RoleState, "currentRole" | "customPermissions">;

export const useRoleStore = create<RoleState>()(
  persist<RoleState>(
    (set, get) => ({
      currentRole: DEFAULT_ROLE,
      permissions: [...DEFAULT_ROLE_PERMISSIONS[DEFAULT_ROLE]],
      customPermissions: null,
      setRole: (role: DemoRoleKey) =>
        set({
          currentRole: role,
          permissions: resolvePermissions(role, get().customPermissions),
        }),
      setCustomPermissions: (role: string, perms: PermissionKey[]) => {
        const next = { ...(get().customPermissions ?? {}), [role]: perms };
        const update: Partial<RoleState> = { customPermissions: next };
        if (get().currentRole === role) {
          update.permissions = [...perms];
        }
        set(update);
      },
      clearCustomPermissions: (role: string) => {
        const prev = get().customPermissions;
        if (!prev) return;
        const { [role]: _, ...rest } = prev;
        const next = Object.keys(rest).length > 0 ? rest : null;
        const update: Partial<RoleState> = { customPermissions: next };
        if (get().currentRole === role) {
          update.permissions = [...DEFAULT_ROLE_PERMISSIONS[role as DemoRoleKey]];
        }
        set(update);
      },
    }),
    {
      name: "bizop-dev-role",
      storage: createJSONStorage(() => localStorage),
      partialize: (state: RoleState): PersistedRoleState => ({
        currentRole: state.currentRole,
        customPermissions: state.customPermissions,
      }),
      onRehydrateStorage: () => (state: RoleState | undefined) => {
        if (!state) return;

        // Validate the persisted role is still a known selectable role.
        // If someone has stale / corrupted data we just fall back to the
        // default rather than logging scary warnings.
        if (!VALID_DEMO_ROLES.has(state.currentRole)) {
          state.currentRole = DEFAULT_ROLE;
          state.customPermissions = null;
        }

        // Always re-derive permissions from the canonical RBAC table so
        // any server-side permission changes take effect immediately.
        state.permissions = resolvePermissions(
          state.currentRole,
          state.customPermissions,
        );
      },
    } as Parameters<typeof persist>[1],
  ),
);
